import type { ReactElement } from 'react'
import type { SalesOrderListItem } from '@/lib/services/sales-orders/sales-orders.types'
import {
  useMarkFulfilledOrderAction,
  type MarkFulfilledOrder,
  type MarkFulfilledOrderAction,
} from '@/components/forms/Orders/MarkFulfilledForm'
import ActionsMenu, { useActionMenu } from '@/components/tables/ActionsMenu'
import { DynamicIcon } from '@/components/ui/DynamicIcon'
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { Can } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import { SalesOrderFulfillmentStatus } from '@/lib/enums/SalesOrderFulfillmentStatus'
import { SalesOrderRefundStatus } from '@/lib/enums/SalesOrderRefundStatus'
import { useUpdateSalesOrderMutation } from '@/lib/services/sales-orders/sales-orders.query'
import { Mail, MailOpen } from 'lucide-react'
import { canUpdateSalesOrderFulfillment, isCancelledSalesOrder } from './sales-order-presentation'

type SalesOrderActionAvailability = Pick<
  SalesOrderListItem,
  'fulfillment_status' | 'fulfilled_at' | 'refund_status' | 'cancelled_at'
>
type SalesOrderActionOrder = Parameters<typeof canUpdateSalesOrderFulfillment>[0] &
  Pick<SalesOrderListItem, 'id' | 'organization_id' | 'fulfilled_at' | 'read_at'>
interface SalesOrderActionMenuProps {
  order: SalesOrderActionOrder
  markFulfilledAction?: MarkFulfilledOrderAction | null
  canEditFulfillment?: boolean
  canEditSalesOrderDetails?: boolean
}

export function shouldShowSalesOrderActions(order: SalesOrderActionAvailability): boolean {
  return !(
    order.fulfillment_status === SalesOrderFulfillmentStatus.Fulfilled.Value ||
    Boolean(order.fulfilled_at) ||
    order.refund_status === SalesOrderRefundStatus.Refunded.Value ||
    isCancelledSalesOrder(order)
  )
}

export function shouldShowMarkFulfilledAction(order: SalesOrderActionOrder): boolean {
  return shouldShowSalesOrderActions(order) && canUpdateSalesOrderFulfillment(order)
}

export function useSalesOrderActionMenu(order: MarkFulfilledOrder): MarkFulfilledOrderAction {
  return useMarkFulfilledOrderAction(order)
}

export function SalesOrderActionsMenu({
  row,
  markFulfilledAction,
}: {
  row: { original: SalesOrderActionOrder }
  markFulfilledAction?: MarkFulfilledOrderAction | null
}): ReactElement {
  return (
    <Can do="edit" on="sales-orders" field="fulfillment" passThrough>
      {(canEditFulfillment) => (
        <Can do="edit" on="sales-orders" field="details" passThrough>
          {(canEditSalesOrderDetails) => {
            const order = row.original
            const canShowFulfillmentAction =
              canEditFulfillment && markFulfilledAction && shouldShowMarkFulfilledAction(order)
            const canShowReadAction = canEditSalesOrderDetails && shouldShowSalesOrderActions(order)

            if (!canShowFulfillmentAction && !canShowReadAction) {
              return null
            }

            return (
              <ActionsMenu row={row}>
                <SalesOrderActionMenuItems
                  order={order}
                  markFulfilledAction={markFulfilledAction}
                  canEditFulfillment={canEditFulfillment}
                  canEditSalesOrderDetails={canEditSalesOrderDetails}
                />
              </ActionsMenu>
            )
          }}
        </Can>
      )}
    </Can>
  )
}

export function SalesOrderActionMenuItems({
  order,
  markFulfilledAction,
  canEditFulfillment = true,
  canEditSalesOrderDetails = true,
}: SalesOrderActionMenuProps): ReactElement | null {
  if (!shouldShowSalesOrderActions(order)) {
    return null
  }

  const showFulfillmentActions =
    canEditFulfillment && markFulfilledAction && shouldShowMarkFulfilledAction(order)

  return (
    <>
      {showFulfillmentActions ? (
        <>
          <DropdownMenuLabel>Set Status</DropdownMenuLabel>
          {markFulfilledAction.actions.map((workflowAction) => (
            <MarkFulfilledAction
              key={workflowAction.targetStatus}
              action={markFulfilledAction}
              workflowAction={workflowAction}
            />
          ))}
        </>
      ) : null}
      {showFulfillmentActions && canEditSalesOrderDetails ? <DropdownMenuSeparator /> : null}
      {canEditSalesOrderDetails ? <MarkAsReadAction order={order} /> : null}
    </>
  )
}

function MarkFulfilledAction({
  action,
  workflowAction,
}: {
  action: MarkFulfilledOrderAction
  workflowAction: MarkFulfilledOrderAction['actions'][number]
}): ReactElement {
  const { setIsDropdownOpen } = useActionMenu()

  return (
    <DropdownMenuItem
      disabled={action.isPending}
      onSelect={(event) => {
        event.preventDefault()
        setIsDropdownOpen(false)
        Promise.resolve(action.requestMarkFulfilled(workflowAction.targetStatus)).catch(() => {})
      }}
    >
      <DynamicIcon iconName={workflowAction.iconName} className="size-4" />
      {workflowAction.menuLabel}
    </DropdownMenuItem>
  )
}

function MarkAsReadAction({
  order,
}: {
  order: Pick<SalesOrderListItem, 'id' | 'organization_id' | 'read_at'>
}): ReactElement {
  const mutation = useUpdateSalesOrderMutation({ organizationId: order.organization_id })

  const handleMarkAsRead = () => {
    mutation.mutate({ id: order.id, read_at: dayjs().toISOString() })
  }

  const handleMarkAsUnread = () => {
    mutation.mutate({ id: order.id, read_at: null })
  }

  return order.read_at ? (
    <DropdownMenuItem onClick={handleMarkAsUnread}>
      <Mail className="size-4" />
      Mark as Unread
    </DropdownMenuItem>
  ) : (
    <DropdownMenuItem onClick={handleMarkAsRead}>
      <MailOpen className="size-4" />
      Mark as Read
    </DropdownMenuItem>
  )
}
